"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  ThemeIcon,
  List,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";

export default function TrueSummerVsCoolSummerContent() {
  const t = useTranslations("trueSummerVsCoolSummer");

  const trueSummerData = colorSeasons.SUMMER.subseasons.TRUE;
  const coolSummerData = colorSeasons.SUMMER.subseasons.COOL;

  const comparisonPoints = [
    {
      aspect: "Color Temperature",
      trueSummer: "Balanced cool - the 'true' representation of summer",
      coolSummer: "Intensely cool - the coolest of all summer subtypes",
      icon: IconPalette,
    },
    {
      aspect: "Saturation",
      trueSummer: "Medium saturation with balanced, clear colors",
      coolSummer: "Can handle slightly more saturated cool colors",
      icon: IconEye,
    },
    {
      aspect: "Best For",
      trueSummer: "Those with balanced cool coloring, medium contrast",
      coolSummer: "Those with very cool coloring, pink/blue undertones",
      icon: IconSparkles,
    },
    {
      aspect: "Avoid",
      trueSummer: "Warm colors, overly muted or overly bright shades",
      coolSummer: "Any warm tones, especially yellow-based colors",
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            True Summer vs Cool Summer
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Both are cool summer subtypes. True Summer is the balanced "classic" summer, while Cool Summer is the iciest.
            Learn which matches your natural coloring.
          </Text>
        </Box>

        {/* Quick Comparison Overview */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Quick Comparison
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{trueSummerData.emoji}</Text>
                <Title order={3}>True Summer</Title>
                <Text size="sm" c="dimmed">
                  The balanced summer. Cool with medium saturation and classic summer colors.
                </Text>
                <Badge color="blue" variant="light">
                  Cool + Balanced + Classic
                </Badge>
                <Group gap="xs" justify="center">
                  {trueSummerData.primaryColors.slice(0, 5).map((color, i) => (
                    <Box key={i} w={24} h={24} style={{ background: color.hex, borderRadius: 4 }} />
                  ))}
                </Group>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{coolSummerData.emoji}</Text>
                <Title order={3}>Cool Summer</Title>
                <Text size="sm" c="dimmed">
                  The iciest summer. Very cool undertones with slightly more color intensity.
                </Text>
                <Badge color="blue" variant="light">
                  Very Cool + Icy + Clear
                </Badge>
                <Group gap="xs" justify="center">
                  {coolSummerData.primaryColors.slice(0, 5).map((color, i) => (
                    <Box key={i} w={24} h={24} style={{ background: color.hex, borderRadius: 4 }} />
                  ))}
                </Group>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Key Differences */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Key Differences
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="blue" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      True Summer:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.trueSummer}
                    </Text>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      Cool Summer:
                    </Text>
                    <Text size="sm">{point.coolSummer}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Color Palette Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Color Palette Comparison
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} mb="md" ta="center">True Summer Colors</Title>
              <Text size="sm" c="dimmed" ta="center" mb="md">Balanced cool tones</Text>
              <SimpleGrid cols={4} spacing="xs">
                {trueSummerData.primaryColors.slice(0, 8).map((color, index) => (
                  <Box key={index}>
                    <Box
                      style={{
                        backgroundColor: color.hex,
                        height: 40,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Text size="xs" ta="center" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center">Cool Summer Colors</Title>
              <Text size="sm" c="dimmed" ta="center" mb="md">Icy cool tones</Text>
              <SimpleGrid cols={4} spacing="xs">
                {coolSummerData.primaryColors.slice(0, 8).map((color, index) => (
                  <Box key={index}>
                    <Box
                      style={{
                        backgroundColor: color.hex,
                        height: 40,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Text size="xs" ta="center" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </SimpleGrid>
        </Card>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Tell the Difference at Home
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">The Pink Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare a balanced rose pink vs an icy fuchsia pink:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>True Summer:</strong> The rose pink feels balanced and flattering</List.Item>
                <List.Item><strong>Cool Summer:</strong> The icy fuchsia makes your skin glow</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">The Silver Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare brushed silver vs bright polished silver:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>True Summer:</strong> Both work, brushed may feel softer</List.Item>
                <List.Item><strong>Cool Summer:</strong> Bright polished silver is stunning on you</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              Still unsure? These tests can be tricky in artificial lighting or with makeup on. A photo analysis removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Want a Definitive Answer?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get a confident result — no more guessing between True Summer and Cool Summer.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Confirm My Season
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/summer/true"
                size="sm"
                color="gray"
              >
                View True Summer Palette →
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/summer/cool"
                size="sm"
                color="gray"
              >
                View Cool Summer Palette →
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
